import { Provider } from './provider';
import { Status } from './status';

export enum ResolutionMode {
  NEW_SALE = 'NEW_SALE',
  EXISTING_PAYMENT = 'EXISTING_PAYMENT',
}

export enum OperationType {
  SALE = 'SALE',
  RENEWAL = 'RENEWAL',
  CHARGE = 'CHARGE',
  RETRY = 'RETRY',
  REFUND = 'REFUND',
}

export enum RoutingReason {
  REQUESTED_INSTANCE = 'REQUESTED_INSTANCE',
  POLICY_DISTRIBUTOR_RULE = 'POLICY_DISTRIBUTOR_RULE',
  DISTRIBUTOR_RULE = 'DISTRIBUTOR_RULE',
  POLICY_RULE = 'POLICY_RULE',
  TENANT_DEFAULT = 'TENANT_DEFAULT',
  PLATFORM_SHARED_AUTHNET = 'PLATFORM_SHARED_AUTHNET',
  PERSISTED_ASSIGNMENT = 'PERSISTED_ASSIGNMENT',
  LEGACY_COMPATIBILITY = 'LEGACY_COMPATIBILITY',
  BATCH_UNANIMOUS = 'BATCH_UNANIMOUS',
  RENEWAL_AFFINITY = 'RENEWAL_AFFINITY',
}

/** Reasons allowed when an ordinary new sale receives its immutable gateway assignment. */
export type NewPaymentRoutingReason =
  | RoutingReason.POLICY_DISTRIBUTOR_RULE
  | RoutingReason.DISTRIBUTOR_RULE
  | RoutingReason.POLICY_RULE
  | RoutingReason.PLATFORM_SHARED_AUTHNET
  | RoutingReason.TENANT_DEFAULT
  | RoutingReason.BATCH_UNANIMOUS;

/**
 * Routing provenance stored on a payment. Legacy compatibility is migration-only; new payments
 * must use a sale reason or explicit renewal affinity.
 */
export type PersistedPaymentRoutingReason =
  | NewPaymentRoutingReason
  | RoutingReason.RENEWAL_AFFINITY
  | RoutingReason.LEGACY_COMPATIBILITY;

const NEW_PAYMENT_ROUTING_REASONS: readonly NewPaymentRoutingReason[] = [
  RoutingReason.POLICY_DISTRIBUTOR_RULE,
  RoutingReason.DISTRIBUTOR_RULE,
  RoutingReason.POLICY_RULE,
  RoutingReason.PLATFORM_SHARED_AUTHNET,
  RoutingReason.TENANT_DEFAULT,
  RoutingReason.BATCH_UNANIMOUS,
];

const PERSISTED_PAYMENT_ROUTING_REASONS: readonly PersistedPaymentRoutingReason[] = [
  ...NEW_PAYMENT_ROUTING_REASONS,
  RoutingReason.RENEWAL_AFFINITY,
  RoutingReason.LEGACY_COMPATIBILITY,
];

export function isNewPaymentRoutingReason(reason: unknown): reason is NewPaymentRoutingReason {
  return NEW_PAYMENT_ROUTING_REASONS.includes(reason as NewPaymentRoutingReason);
}

export function isPersistedPaymentRoutingReason(reason: unknown): reason is PersistedPaymentRoutingReason {
  return PERSISTED_PAYMENT_ROUTING_REASONS.includes(reason as PersistedPaymentRoutingReason);
}

interface BaseResolutionContext {
  tenantId: string;
}

export interface NewSaleResolutionContext extends BaseResolutionContext {
  mode: ResolutionMode.NEW_SALE;
  operation: OperationType.SALE;
  policyId: string;
  distributorId: string | null;
  requestedGatewayInstanceId?: string;
}

export interface ExistingPaymentResolutionContext extends BaseResolutionContext {
  mode: ResolutionMode.EXISTING_PAYMENT;
  operation: OperationType.CHARGE | OperationType.RETRY | OperationType.REFUND | OperationType.RENEWAL;
  paymentId: string;
  assignedGatewayInstanceId: string | null;
  assignedGatewayRoutingReason: PersistedPaymentRoutingReason | null;
}

export type ResolutionContext = NewSaleResolutionContext | ExistingPaymentResolutionContext;

/** Credential-free gateway identity returned to payment orchestration. */
export interface ResolvedInstanceReference {
  id: string;
  provider: Provider;
  name: string;
  status: Status;
}

export interface Resolution {
  instance: ResolvedInstanceReference;
  reason: RoutingReason;
}
